import { INIT_DEBUGGER } from '../../core/debug/debugMacros';

import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import HLS, { Fragment, type MediaPlaylist as HlsAudioTrack } from 'hls.js';

import { AsyncResult, Maybe } from '../../core';
import { type MediaInfo, type JobModel } from '../../entities';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { IPlayerAdapter } from '../player';
import { HTMLMediaNetworkState, HTMLMediaReadyState } from './htmlMediaSchema';
import { getBufferedTimeFromRanges, getSeekableTimeFromRanges, getSeekableWindow, timeRangesHas } from '../../core/media/mediaUtils';
import { ITimeRegion } from '../../core/time/timeSchema';
import { createArray } from '../../core/array';
import { HlsManifestStatus, SeekParams, SetAutoplayParams, SetControlsParams, SetPlaybackRateParams, SetVolumeParams } from './playerAdapterSchema';
import { PlayerAudioTracksState } from './playerAudioTracksState';

import { syncState, handleExternalCaptions, handleDOMEvent, mountVideoElement, unmountVideoElement, reattachDOMVideo as reattachDOMVideoFn, tryInitPlayer, isValidSeekTime as isValidSeekTimeFn, playMedia, pauseMedia, seekMedia, setMediaVolume, setMediaPlaybackRate, setMediaControls, setMediaAutoplay, invokeMediaAction, VIDEO_DOM_EVENTS } from './playerMediaState';

export { VIDEO_DOM_EVENTS };
export { HlsManifestStatus };

type Props = {
  media: MediaInfo,
  jobId: string | null,
};

export class PlayerAdapterState
  extends StoreNode
  implements IPlayerAdapter {

  readonly nodeType: 'PlayerAdapter' = 'PlayerAdapter';

  constructor(store: Store, props?: BindingProps<Props>) {
    super(store, props);
    makeObservable<this, 'handleMediaChange'>(this, {
      media: computed,
      jobId: computed,
      job: computed,
      isInitialized: observable,
      currentFragment: observable,
      initFragment: observable,
      fragmentDuration: observable,
      initPTS: observable,
      isLiveStream: observable,
      videoElement: observable,
      duration: observable,
      hasDuration: computed,
      error: observable,
      readyState: observable,
      networkState: observable,
      controls: observable,
      autoplay: observable,
      autoplayAfterRetry: observable,
      currentTime: observable,
      volume: observable,
      isMuted: observable,
      playbackRate: observable,
      playedTimeRanges: observable,
      bufferedTimeRanges: observable,
      seekableTimeRanges: observable,
      isPaused: observable,
      isSeeking: observable,
      isReady: observable,
      timer: observable,
      bufferedTime: computed,
      seekableTime: computed,
      seekableWindow: computed,
      seekableStart: computed,
      seekableEnd: computed,
      initPtsInSec: computed,
      isEnded: observable,
      hlsManifestStatus: observable,
      isHlsManifestParsed: computed,
      isHlsManifestLoaded: computed,
      isHlsManifestError: computed,
      isHlsManifestIdle: computed,
      clientWidth: observable,
      clientHeight: observable,
      videoWidth: observable,
      videoHeight: observable,
      hls: observable,
      initialCurrentTime: observable,
      initialVolume: observable,
      initialPlaybackRate: observable,
      initialIsPlaying: observable,
      initialControls: observable,
      initialAutoplay: observable,
      isWaiting: computed,
      isMetadataLoaded: computed,
      canInvokePlay: computed,
      isPlaying: computed,
      isActuallyPlaying: computed,
      handleDOMEventBound: action,
      syncStateFromDom: action,
      handleMediaChange: action,
      mounted: action,
      unmounted: action,
      reattachDOMVideo: action,
      handleExternalCaptions: action,
      invoke: action,
      play: action,
      pause: action,
      seek: action,
      setVolume: action,
      setPlaybackRate: action,
      setControls: action,
      setAutoplay: action,
    });

    INIT_DEBUGGER(this, {
      color: 'purple',
      enableTrace: false
    });

    this.onPropChange('media',
      this.handleMediaChange);

    this.playerAudioTracks = new PlayerAudioTracksState(this.store, {
      adapter: this
    });
  }

  readonly playerAudioTracks: PlayerAudioTracksState;

  get media(): MediaInfo | null {
    return this.resolvedProps.media;
  }

  get jobId(): string {
    return this.resolvedProps.jobId;
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  isInitialized = false;
  readonly fragments: IObservableArray<Fragment> = createArray<Fragment>(true) as IObservableArray;
  currentFragment: Fragment | null = null;
  initFragment: Fragment | null = null;
  fragmentDuration: number | null = null;
  initPTS: number | null = null;
  isLiveStream: boolean = false;

  videoElement: HTMLVideoElement | null = null;

  duration: number = 0;
  get hasDuration() {
    return (Number.isFinite(this.duration) && this.duration > 0);
  }

  error: MediaError | null = null;
  readyState: HTMLMediaReadyState = HTMLMediaReadyState.HAVE_NOTHING;
  networkState: HTMLMediaNetworkState = HTMLMediaNetworkState.NETWORK_EMPTY;

  controls: boolean = false;
  autoplay: boolean = false;
  autoplayAfterRetry: boolean = false;
  currentTime: number = 0;
  volume: number = 0;
  isMuted: boolean = false;
  playbackRate: number = 1.0;

  playedTimeRanges: TimeRanges | null = null;
  bufferedTimeRanges: TimeRanges | null = null;
  seekableTimeRanges: TimeRanges | null = null;
  isPaused = false;
  isSeeking: boolean = false;
  isReady: boolean = false;
  timer: NodeJS.Timeout | null = null;

  get bufferedTime(): number {
    return getBufferedTimeFromRanges(this.bufferedTimeRanges, this.currentTime) ?? 0;
  }

  get seekableTime(): number {
    return getSeekableTimeFromRanges(this.seekableTimeRanges, this.currentTime) ?? 0;
  }

  get seekableWindow(): ITimeRegion | null {
    return getSeekableWindow(this.seekableTimeRanges);
  }

  get seekableStart(): number {
    return this.seekableWindow?.startTime ?? 0;
  }

  get seekableEnd(): number {
    return this.seekableWindow?.endTime ?? 0;
  }

  get initPtsInSec(): number {
    if (this.initPTS)
      return this.initPTS / 90000;

    return 0;
  }

  isEnded: boolean = false;

  hlsManifestStatus: HlsManifestStatus = HlsManifestStatus.None;

  get isHlsManifestParsed(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.Parsed;
  }

  get isHlsManifestLoaded(): boolean {
    return [HlsManifestStatus.Loaded, HlsManifestStatus.Parsed].includes(this.hlsManifestStatus);
  }

  get isHlsManifestError(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.Error;
  }

  get isHlsManifestIdle(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.None;
  }

  clientWidth: number | null = null;
  clientHeight: number | null = null;
  videoWidth: number | null = null;
  videoHeight: number | null = null;

  hls: HLS | null = null;

  initialCurrentTime: number | null = null;
  initialVolume: number | null = null;
  initialPlaybackRate: number | null = null;
  initialIsPlaying: boolean | null = null;
  initialControls: boolean | null = null;
  initialAutoplay: boolean | null = null;

  get isWaiting(): boolean {
    if (this.readyState === null)
      return false;

    return (
      this.readyState <= HTMLMediaReadyState.HAVE_CURRENT_DATA &&
      !this.isPaused && (
        this.isSeeking ||
        !timeRangesHas(this.bufferedTimeRanges, this.currentTime)));
  }

  get isMetadataLoaded(): boolean {
    return this.readyState >= HTMLMediaReadyState.HAVE_METADATA;
  }

  get canInvokePlay(): boolean {
    return this.isMetadataLoaded && !!this.videoElement;
  }

  get isPlaying(): boolean {
    return (
      !!this.videoElement &&
      !this.isPaused);
  }

  get isActuallyPlaying(): boolean {
    return (
      !!this.videoElement &&
      this.readyState >= HTMLMediaReadyState.HAVE_FUTURE_DATA &&
      !this.isPaused);
  }

  get canPlay() {
    if (this.readyState === null)
      return false;
    return this.readyState >= HTMLMediaReadyState.HAVE_FUTURE_DATA;
  }

  get canPlayThrough() {
    if (this.readyState === null)
      return false;
    return this.readyState >= HTMLMediaReadyState.HAVE_ENOUGH_DATA;
  }

  // Bound event handler for DOM events (used by mount/unmount)
  handleDOMEventBound = (evt: Event) => {
    handleDOMEvent(this, evt);
  }

  syncStateFromDom() {
    syncState(this);
  }

  private handleMediaChange = () => {
    if (this.isInitialized && !this.isHlsManifestLoaded) {
      this.reattachDOMVideo();
    }
    else
      tryInitPlayer(this);
  }

  mounted(video: HTMLVideoElement | null) {
    mountVideoElement(this, video!);
  }

  unmounted() {
    unmountVideoElement(this);
  }

  reattachDOMVideo() {
    reattachDOMVideoFn(this);
  }

  setHlsAudioTrack = (track: HlsAudioTrack) => {
    if (!this.hls)
      return;

    this.hls.audioTrack = track.id;
  }

  isValidSeekTime(time?: Maybe<number>): boolean {
    return isValidSeekTimeFn(this, time);
  }

  handleExternalCaptions = (video: HTMLVideoElement) => {
    handleExternalCaptions(this, video);
  }

  invoke = async (type: string, payload?: unknown) => {
    await invokeMediaAction(this, type, payload as SeekParams | SetVolumeParams | SetPlaybackRateParams | SetControlsParams | SetAutoplayParams | null);
  }

  // #region Media API

  async play(): AsyncResult<boolean> {
    return playMedia(this);
  }

  pause() {
    pauseMedia(this);
  }

  seek(params: SeekParams) {
    seekMedia(this, params);
  }

  setVolume(params: SetVolumeParams) {
    setMediaVolume(this, params);
  }

  setPlaybackRate(params: SetPlaybackRateParams) {
    setMediaPlaybackRate(this, params);
  }

  setControls(params: SetControlsParams) {
    setMediaControls(this, params);
  }

  setAutoplay(params: SetAutoplayParams) {
    setMediaAutoplay(this, params);
  }
  // #endregion
}
